import { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import '../../shared/ui/ag-grid-theme.css'
import type {
  ColDef,
  ColumnResizedEvent,
  DragStoppedEvent,
  GridApi,
  GridReadyEvent,
  SelectionChangedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import { AG_GRID_LOCALE_RU } from '../../shared/ag-grid/locale'
import { useAuthStore } from '../auth/authStore'
import { PageLoader } from '../../shared/ui/Spinner'
import { EmptyState } from '../../shared/ui/EmptyState'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { toast } from '../../shared/ui/toastStore'
import { confirm } from '../../shared/ui/confirmStore'
import { CheckboxSetFilter } from '../table/CheckboxSetFilter'
import {
  createSupplier,
  deleteSupplier,
  getSupplierBuyers,
  getSuppliers,
  importSuppliers,
  restoreSupplier,
  updateSupplier,
  type SupplierBody,
} from '../../shared/api/suppliers.api'
import { SupplierEditModal } from './SupplierEditModal'
import { SupplierMergeModal } from './SupplierMergeModal'
import { buildSupplierColDefs, toGridRow, type SupplierGridRow } from './suppliersColDefs'
import { parseSuppliersExcel } from './suppliersImport'

ModuleRegistry.registerModules([AllCommunityModule])

// Конверт ошибок API — { success: false, error: { message } } (русские тексты).
const errText = (e: unknown): string => {
  const data = (e as { response?: { data?: { error?: { message?: string }; message?: string } } })
    ?.response?.data
  return data?.error?.message ?? data?.message ?? (e as Error)?.message ?? 'Ошибка'
}

// Per-user сохранение вида колонок (порядок/ширина/сортировка).
const COL_STATE_KEY = (userId: string) => `crm_suppliers_cols_v1_${userId}`

// Раздел «Поставщики» — справочник по файлу РОЗ «Условия/Объём CNY» (Р12).
// Кто кого видит — решает сервер (suppliers.access.ts): закупщик — своих,
// РОЗ — импорт и смешанных, РОП, админ и ГД — всех. Создание, импорт и
// объединение дублей — РОЗ/ADMIN; скрыть/вернуть и ответственного — по правам
// строки от сервера (РОП — только поставщиков РФ). Клик по имени → карточка.
export default function SuppliersPage() {
  const user = useAuthStore((s) => s.user)
  const canManage = user?.role === 'ADMIN' || user?.role === 'ROS'
  // Режим «Скрытые» и колонка «скрыть/вернуть»: закупщику и ГД не нужны.
  const canCleanUp = canManage || user?.role === 'ROP'
  const qc = useQueryClient()
  const navigate = useNavigate()

  const [search, setSearch] = useState('')
  // Режим «Скрытые»: показывает вычищенных поставщиков с кнопкой «Вернуть».
  const [showHidden, setShowHidden] = useState(false)
  // Фильтр «Без условий оплаты»: где РОЗ ещё не заполнила условия (C3).
  const [noPaymentOnly, setNoPaymentOnly] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [mergeOpen, setMergeOpen] = useState(false)
  const [selected, setSelected] = useState<SupplierGridRow[]>([])
  const [importing, setImporting] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const gridApiRef = useRef<GridApi | null>(null)

  const suppliersQ = useQuery({
    queryKey: ['suppliers', { showHidden }],
    queryFn: () => getSuppliers({ showHidden }),
  })
  const buyersQ = useQuery({ queryKey: ['suppliers', 'buyers'], queryFn: getSupplierBuyers })
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['suppliers'] })

  const assignMut = useMutation({
    mutationFn: (vars: { id: string; buyerId: string | null }) =>
      updateSupplier(vars.id, { responsibleBuyerId: vars.buyerId }),
    onSuccess: invalidate,
    onError: (e: unknown) => toast.error(errText(e)),
  })

  const noPaymentFilter = noPaymentOnly && !showHidden
  const rows = useMemo(() => {
    const all = (suppliersQ.data?.suppliers ?? []).map(toGridRow)
    const list = noPaymentFilter ? all.filter((s) => !s.paymentType) : all
    const q = search.trim().toLowerCase()
    if (!q) return list
    // Поиск и по алиасам: «Wtron» находит WTR, в который он влит.
    return list.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.aliases.some((a) => a.aliasName.toLowerCase().includes(q)),
    )
  }, [suppliersQ.data, search, noPaymentFilter])
  const hasSearch = search.trim().length > 0
  const noPaymentCount = useMemo(
    () => (suppliersQ.data?.suppliers ?? []).filter((s) => !s.paymentType).length,
    [suppliersQ.data],
  )

  const hiddenCount = suppliersQ.data?.hiddenCount ?? 0
  const buyers = buyersQ.data ?? []

  const handleHide = useCallback(async (s: SupplierGridRow) => {
    const ok = await confirm({
      title: `Скрыть поставщика «${s.name}»?`,
      message:
        'Квоты, заявки и вся история не пострадают — поставщик исчезнет только из справочника. Вернуть можно через режим «Скрытые».',
      confirmText: 'Скрыть',
      danger: true,
    })
    if (!ok) return
    try {
      await deleteSupplier(s.id)
      invalidate()
      toast.success(`«${s.name}» скрыт из справочника`)
    } catch (e) {
      toast.error(errText(e))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRestore = useCallback(async (s: SupplierGridRow) => {
    try {
      await restoreSupplier(s.id)
      invalidate()
      toast.success(`«${s.name}» возвращён в справочник`)
    } catch (e) {
      toast.error(errText(e))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const colDefs = useMemo<ColDef<SupplierGridRow>[]>(
    () =>
      buildSupplierColDefs({
        showActions: canCleanUp,
        showHidden,
        buyers,
        onOpen: (id) => navigate(`/suppliers/${id}`),
        onAssign: (id, buyerId) => assignMut.mutate({ id, buyerId }),
        onHide: (row) => void handleHide(row),
        onRestore: (row) => void handleRestore(row),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canCleanUp, showHidden, buyers],
  )

  // Per-user вид колонок: save на drag/resize.finished/sort, restore в onGridReady.
  const saveColState = useCallback(
    (api: GridApi) => {
      if (!user?.id) return
      try {
        localStorage.setItem(COL_STATE_KEY(user.id), JSON.stringify(api.getColumnState()))
      } catch {
        /* ignore */
      }
    },
    [user?.id],
  )
  const onGridReady = useCallback(
    (e: GridReadyEvent) => {
      gridApiRef.current = e.api
      if (!user?.id) return
      const saved = localStorage.getItem(COL_STATE_KEY(user.id))
      if (saved) {
        try {
          e.api.applyColumnState({ state: JSON.parse(saved), applyOrder: true })
        } catch {
          /* ignore */
        }
      }
    },
    [user?.id],
  )

  const onSelectionChanged = useCallback((e: SelectionChangedEvent<SupplierGridRow>) => {
    setSelected(e.api.getSelectedRows())
  }, [])

  const handleSave = async (body: SupplierBody) => {
    await createSupplier(body)
    toast.success('Поставщик добавлен в справочник')
    invalidate()
  }

  const handleImportFile = async (file: File) => {
    setImporting(true)
    try {
      const parsed = await parseSuppliersExcel(file)
      if (parsed.length === 0) {
        toast.error('В файле не найдено ни одной строки с поставщиком')
        return
      }
      const res = await importSuppliers(parsed)
      const parts = [`создано ${res.created}`, `обновлено ${res.updated}`]
      if (res.unknownBuyers.length > 0)
        parts.push(`не найдены закупщики: ${res.unknownBuyers.join(', ')}`)
      toast.success(`Импорт завершён: ${parts.join(', ')}`)
      invalidate()
    } catch (e) {
      toast.error(errText(e))
    } finally {
      setImporting(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  if (!user) return null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-baseline gap-3">
          <h1 className="text-xl font-semibold text-gray-900">
            {showHidden ? 'Скрытые поставщики' : 'Поставщики'}
          </h1>
          <span className="text-sm text-gray-500">
            {showHidden ? 'скрыто' : 'в справочнике'}: {suppliersQ.data?.suppliers.length ?? 0}
          </span>
        </div>
        {canManage && !showHidden && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={importing}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 transition-colors text-gray-700 disabled:opacity-50"
            >
              {importing ? 'Импорт…' : 'Импорт из Excel'}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) void handleImportFile(f)
              }}
            />
            <button
              onClick={() => setCreateOpen(true)}
              className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors"
            >
              + Поставщик
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по названию и алиасам…"
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white w-full max-w-md"
        />
        {!showHidden && (noPaymentCount > 0 || noPaymentOnly) && (
          <button
            onClick={() => setNoPaymentOnly((v) => !v)}
            title="Поставщики, у которых не указаны условия оплаты"
            className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
              noPaymentOnly
                ? 'bg-brand-600 text-white border-brand-600 hover:bg-brand-700'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Без условий оплаты ({noPaymentCount})
          </button>
        )}
        {canCleanUp && (hiddenCount > 0 || showHidden) && (
          <button
            onClick={() => {
              setShowHidden((v) => !v)
              setSelected([])
            }}
            className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
              showHidden
                ? 'bg-brand-600 text-white border-brand-600 hover:bg-brand-700'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {showHidden ? '← К справочнику' : `Скрытые (${hiddenCount})`}
          </button>
        )}
        {canManage && !showHidden && selected.length >= 2 && (
          <button
            onClick={() => setMergeOpen(true)}
            className="px-3 py-1.5 bg-amber-500 text-white text-sm rounded-lg hover:bg-amber-600 transition-colors"
            title="Объединить дубли: выбранные станут одним поставщиком с алиасами"
          >
            Объединить ({selected.length})
          </button>
        )}
      </div>

      {suppliersQ.isLoading ? (
        <PageLoader />
      ) : suppliersQ.isError ? (
        // Иначе ошибка загрузки читается как «Справочник пуст».
        <ErrorMessage error={suppliersQ.error} />
      ) : rows.length === 0 ? (
        hasSearch ? (
          <EmptyState title="Ничего не найдено" description="Измените поисковый запрос." />
        ) : noPaymentFilter ? (
          <EmptyState
            title="Условия оплаты заполнены у всех"
            description="Снимите фильтр «Без условий оплаты», чтобы увидеть весь справочник."
          />
        ) : (
          <EmptyState
            title={showHidden ? 'Скрытых поставщиков нет' : 'Справочник пуст'}
            description={
              showHidden
                ? 'Всё, что скрывали при чистке, уже возвращено.'
                : canManage
                  ? 'Добавьте поставщика вручную или импортируйте файл «Условия/Объём CNY».'
                  : 'Поставщиков в справочнике пока нет.'
            }
          />
        )
      ) : (
        <div className="crm-table ag-theme-alpine rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm h-[calc(100vh-260px)]">
          <AgGridReact<SupplierGridRow>
            rowData={rows}
            columnDefs={colDefs}
            defaultColDef={{
              resizable: true,
              sortable: true,
              suppressHeaderMenuButton: true,
              filter: CheckboxSetFilter,
              floatingFilter: false,
            }}
            localeText={AG_GRID_LOCALE_RU}
            getRowId={(p) => p.data.id}
            rowHeight={40}
            headerHeight={40}
            suppressDragLeaveHidesColumns
            suppressCellFocus
            // colDefs пересоздаются (buyers/showHidden) — без этого AG Grid
            // сбрасывал бы сохранённый per-user порядок колонок к дефолту.
            maintainColumnOrder
            {...(canManage
              ? {
                  rowSelection: {
                    mode: 'multiRow' as const,
                    enableClickSelection: false,
                    checkboxes: true,
                    headerCheckbox: true,
                  },
                  onSelectionChanged,
                }
              : {})}
            onGridReady={onGridReady}
            onDragStopped={(e: DragStoppedEvent) => saveColState(e.api)}
            onColumnResized={(e: ColumnResizedEvent) => {
              if (e.finished) saveColState(e.api)
            }}
            onSortChanged={(e: SortChangedEvent) => saveColState(e.api)}
            overlayNoRowsTemplate='<span class="text-sm text-gray-400">Поставщиков не найдено</span>'
          />
        </div>
      )}

      {mergeOpen && (
        <SupplierMergeModal
          rows={selected}
          onClose={() => setMergeOpen(false)}
          onMerged={() => {
            setMergeOpen(false)
            setSelected([])
            gridApiRef.current?.deselectAll()
            invalidate()
          }}
        />
      )}

      {createOpen && (
        <SupplierEditModal
          supplier={null}
          buyers={buyers}
          onSave={handleSave}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </div>
  )
}
